import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/** RL-1782/1783: one role at one branch (or every branch). */
export interface StaffRoleAssignment {
  assignmentId: string;
  role: string;
  roleName: string;
  branch: string | null;
  branchName: string;
  startsAt: string;
  endsAt: string | null;
  editable: boolean;
}

export interface StaffAccessState {
  status: string;
  accessEndsAt: string | null;
  access: 'active' | 'ending' | 'ended';
  sessionsRevoked: number;
}

export interface StaffResetSent {
  delivery: 'emailed' | 'unavailable';
  expiresInMinutes: number;
  link: string | null;
}

function useRefreshStaff() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
}

export function useAddStaffRole(staffId: string) {
  const refresh = useRefreshStaff();
  return useMutation({
    mutationFn: (body: {
      role: string;
      branchCodes: string[];
      reason: string;
    }) =>
      api
        .post<{ roles: StaffRoleAssignment[] }>(
          `/admin/staff/${staffId}/roles`,
          body,
        )
        .then((r) => r.data),
    onSuccess: refresh,
  });
}

export function useRemoveStaffRole(staffId: string) {
  const refresh = useRefreshStaff();
  return useMutation({
    mutationFn: ({
      assignmentId,
      reason,
    }: {
      assignmentId: string;
      reason: string;
    }) =>
      api
        .post<{ roles: StaffRoleAssignment[] }>(
          `/admin/staff/${staffId}/roles/${assignmentId}/remove`,
          { reason },
        )
        .then((r) => r.data),
    onSuccess: refresh,
  });
}

export function useStaffAccess(staffId: string) {
  const refresh = useRefreshStaff();
  return useMutation({
    mutationFn: ({
      action,
      reason,
      at,
    }: {
      action: 'end' | 'restore';
      reason: string;
      at?: string;
    }) =>
      api
        .post<StaffAccessState>(`/admin/staff/${staffId}/access/${action}`, {
          reason,
          ...(at ? { at } : {}),
        })
        .then((r) => r.data),
    onSuccess: refresh,
  });
}

export function useSendStaffReset(staffId: string) {
  return useMutation({
    mutationFn: () =>
      api
        .post<StaffResetSent>(`/admin/staff/${staffId}/password-reset`, {})
        .then((r) => r.data),
  });
}
